import { useEffect, useState } from 'react'
import { Search, ChevronDown } from 'lucide-react'
import { DataTable, PaginationWrapper } from '@/components/organisms'
import { TableSkeleton } from '@/components/ui/table-skeleton'
import { formatCurrency } from '@/utils'
import { Project } from '@/types/project'

interface FundingRoundsTableProps {
  title: string
  navigate: (path: string) => void
  useDataHook: (params: Record<string, any>) => any
  dataKey: string
  showPagination?: boolean
  useUrlParams?: boolean
}

const sortByOptions: Array<{ value: 'ORDERS' | 'PROJECT_NAME' | 'DATE'; label: string }> = [
  { value: 'ORDERS', label: 'Orders' },
  { value: 'PROJECT_NAME', label: 'Project Name' },
  { value: 'DATE', label: 'Date' },
]

const sortDirectionOptions: Array<{ value: 'ASC' | 'DESC'; label: string }> = [
  { value: 'DESC', label: 'Descending' },
  { value: 'ASC', label: 'Ascending' },
]

const ProjectLogo = ({ logo, alt }: { logo?: string | null; alt: string }) => {
  const [imgError, setImgError] = useState(false)

  return (
    <div className="h-8 w-8 rounded-full bg-[var(--base-muted,#f5f5f5)] dark:bg-[#2a2a2a] flex items-center justify-center flex-shrink-0 overflow-hidden">
      {!imgError && logo ? (
        <img
          src={logo}
          alt={alt}
          className="h-8 w-8 rounded-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 leading-none">
          {alt.charAt(0).toUpperCase()}
        </span>
      )}
    </div>
  )
}

const formatTableDate = (date: string | null | undefined) => {
  if (!date) return '－'
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return '－'

  return parsed.toLocaleDateString('en-GB')
}

const FundingRoundsTable = ({
  title,
  navigate,
  useDataHook,
  dataKey: _dataKey,
  showPagination = true,
  useUrlParams: _useUrlParams = false,
}: FundingRoundsTableProps) => {
  const dataHookResult = useDataHook({})

  const {
    projects,
    loading,
    error: _error,
    pagination,
    search,
    sortBy,
    sortDirection,
    setSearch,
    setSortBy,
    setSortDirection,
    goToPage,
  } = dataHookResult

  const [localSearch, setLocalSearch] = useState(search || '')

  useEffect(() => {
    setLocalSearch(search || '')
  }, [search])

  const handleSearchChange = (value: string) => {
    setLocalSearch(value)
    setSearch(value)
  }

  const handleProjectClick = (project: Project) => {
    navigate(`/data-rooms/${project.project_id}`)
  }

  const columns = [
    {
      key: 'project_name',
      title: 'Project',
      width: 'w-[400px]',
      render: (item: Project) => (
        <div className="flex items-center gap-[10px]">
          <div className="size-[26px] shrink-0 overflow-hidden rounded-full bg-[var(--base-muted,#f5f5f5)] dark:bg-[#2a2a2a]">
            <ProjectLogo logo={item.logo} alt={item.project_name} />
          </div>
          <div className="overflow-hidden text-ellipsis whitespace-nowrap font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            {item.project_name}
          </div>
        </div>
      ),
      sortable: true,
    },
    {
      key: 'total_raised',
      title: 'Total Raised',
      width: 'w-[300px]',
      render: (item: Project) => {
        const totalRaised = (item.rounds || []).reduce((sum, round) => sum + round.amount_raised, 0)
        return (
          <span className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            {totalRaised > 0 ? formatCurrency(totalRaised) : '－'}
          </span>
        )
      },
      sortable: true,
    },
    {
      key: 'latest_round',
      title: 'Latest Round',
      width: 'w-[300px]',
      render: (item: Project) => {
        const latestRound = (item.rounds || []).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
        return (
          <span className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            {latestRound?.amount_raised ? formatCurrency(latestRound.amount_raised) : '－'}
          </span>
        )
      },
      sortable: true,
    },
    {
      key: 'valuation',
      title: 'Valuation',
      width: 'w-[300px]',
      render: (item: Project) => {
        const latestRound = (item.rounds || []).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
        return (
          <span className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            {latestRound?.fully_diluted_valuation ? formatCurrency(latestRound.fully_diluted_valuation) : '－'}
          </span>
        )
      },
      sortable: true,
    },
    {
      key: 'date',
      title: 'Data',
      width: 'w-[276px]',
      render: (item: Project) => {
        const latestRound = (item.rounds || []).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
        return (
          <span className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            {formatTableDate(latestRound?.date)}
          </span>
        )
      },
      sortable: true,
    },
  ]

  return (
    <div className="space-y-3">
      <div className="flex h-[52px] items-center justify-between">
        <h3 className="font-plus-jakarta text-[23px] font-semibold leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
          {title}
        </h3>
      </div>

      <div className="border border-solid border-white bg-[rgba(255,255,255,0.32)] dark:border-[#1b1919] dark:bg-[rgba(23,23,23,0.42)]">
        <div className="flex flex-col gap-2 p-2 xl:flex-row xl:items-center xl:justify-between">
          <div className="relative w-full xl:w-[677px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />
            <input
              type="text"
              placeholder="Search by project name"
              value={localSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="h-[52px] w-full bg-[rgba(255,255,255,0.44)] px-10 text-[13px] font-normal leading-[18px] text-[#2d2923] outline-none placeholder:text-[rgba(45,41,35,0.48)] dark:bg-[#1c1c1c] dark:text-[#fdfdfd] dark:placeholder:text-[rgba(253,253,253,0.48)]"
            />
          </div>

          <div className="flex w-full gap-2 xl:w-[488px]">
            <div className="relative min-w-0 flex-1">
              <select
                value={sortBy || 'ORDERS'}
                onChange={(e) => setSortBy((e.target.value as 'DATE' | 'PROJECT_NAME' | 'ORDERS') || 'ORDERS')}
                className="h-[52px] w-full appearance-none bg-[rgba(255,255,255,0.44)] px-4 pr-10 text-[13px] font-normal leading-[18px] text-[#2d2923] outline-none dark:bg-[#1c1c1c] dark:text-[#fdfdfd]"
              >
                {sortByOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            </div>

            <div className="relative min-w-0 flex-1">
              <select
                value={sortDirection || 'DESC'}
                onChange={(e) => setSortDirection((e.target.value as 'ASC' | 'DESC') || 'DESC')}
                className="h-[52px] w-full appearance-none bg-[rgba(255,255,255,0.44)] px-4 pr-10 text-[13px] font-normal leading-[18px] text-[#2d2923] outline-none dark:bg-[#1c1c1c] dark:text-[#fdfdfd]"
              >
                {sortDirectionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            </div>
          </div>
        </div>

        <div className="overflow-clip bg-[rgba(255,255,255,0.2)] dark:bg-[rgba(10,10,10,0.2)]">
          {loading ? (
            <TableSkeleton variant="elevated" rows={5} columns={5} showHeader={false} showFilters={false} />
          ) : (
            <DataTable
              columns={columns}
              data={projects || []}
              keyExtractor={(item) => item.project_id}
              isLoading={false}
              onRowClick={handleProjectClick}
              rowClassName="h-[68px] odd:[&>td]:bg-[rgba(255,255,255,0.28)] dark:[&>td]:!bg-transparent dark:odd:[&>td]:!bg-[rgba(28,28,28,0.8)]"
              className="[&_table]:table-fixed [&_thead_tr]:dark:!border-[#0a0a0a] [&_thead_th]:dark:!bg-[rgba(28,28,28,0.8)] [&_thead_th]:dark:!border-[#0a0a0a] [&_tbody_td]:dark:!border-[#0a0a0a] [&_thead_svg]:dark:!text-[#878787] [&_tbody_tr:hover>td]:dark:!bg-[rgba(253,253,253,0.06)]"
            />
          )}
        </div>
      </div>

      {showPagination && pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center">
          <PaginationWrapper
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={goToPage}
          />
        </div>
      )}
    </div>
  )
}

export default FundingRoundsTable
